import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { Random } from "../shared/util/random";

export type IToast = {
    id: string;
    content: React.ReactNode;
    timer: number;
    createdAt: Date;
}

export type IToasterContext = {
    makeToast: (content: React.ReactNode, timer?: number) => void;
    notifications: IToast[];
    removeNotification: (id: string) => void;
    clearNotifications: () => void;
};

export const ToasterContext = createContext<IToasterContext>({
    makeToast: (content: React.ReactNode, timer?: number) => {},
    notifications: [],
    removeNotification: () => {},
    clearNotifications: () => {},
});

function Toast({toast, onClose}: {
    toast: IToast;
    onClose?: (id: string) => void;
}) {

    const [timer, setTimer] = useState(toast.timer);
    const [paused, setPaused] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const Close = useCallback(() => {
        const a = ref.current;

        if (!a) {
            if (onClose) onClose(toast.id);
            return;
        }

        a.classList.add("close");
        setTimeout(() => {
            if (onClose) onClose(toast.id);
        }, 400);
    }, [ref.current]);

    useEffect(() => {
        if (timer <= 0) {
            Close();
        }
    }, [timer]);

    useEffect(() => {
        const el = ref.current;

        if (!el) return;

        const cb = () => {
            if (paused) return;
            setTimer(prev => Math.max(0, prev - 1/60));
        }

        const mouseenter = (e: MouseEvent) => {
            // const pos = {x: e.clientX, y: e.clientY};
            setPaused(true);
        }

        const mouseleave = (e: MouseEvent) => {
            // const pos = {x: e.clientX, y: e.clientY};
            setPaused(false);
        }

        const a = setInterval(cb, 1000/60);

        el.addEventListener("mouseenter", mouseenter);
        el.addEventListener("mouseleave", mouseleave);

        return () => {
            clearInterval(a);
            el.removeEventListener("mouseenter", mouseenter);
            el.removeEventListener("mouseleave", mouseleave);
        }
    }, [ref.current, paused]);

    return (
        <div ref={ref} className="Toast my-2">
            <div className="inline-block text-left max-w-72 bg-neutral text-neutral-content border border-neutral shadow-lg rounded-md text-sm">
                <div className="flex items-start p-2">
                    <div className="flex-1 whitespace-pre-wrap">
                        {toast.content}
                    </div>
                    <div className="flex-none">
                        <button onClick={Close} className="btn btn-ghost btn-xs"><X size={11} /></button>
                    </div>
                </div>
                <div className="h-[2px] bg-info" style={{
                    width: `${Math.floor(timer/toast.timer*100)}%`
                }}></div>
            </div>
        </div>
    );
}

export function ToasterProvider({children}: {
    children: React.ReactNode;
}) {

    const [toasts, setToasts] = useState<IToast[]>([]);
    const [notifications, setNotifications] = useState<IToast[]>([]);

    function removeNotification(id: string) {
        setNotifications(prev => {
            if (!prev.map(v => v.id).includes(id)) return prev;

            const li = [...prev];
            li.splice(li.map(v => v.id).indexOf(id), 1);

            return li;
        });
    }

    function clearNotifications() {
        setNotifications([]);
    }


    function makeToast(content: React.ReactNode, timer: number = 5) {
        setToasts(prev => [
            ...prev,
            {
                id: Random.string(8),
                content,
                timer,
                createdAt: new Date()
            }
        ]);
        setNotifications(prev => [
            ...prev,
            {
                id: Random.string(8),
                content,
                timer,
                createdAt: new Date()
            }
        ]);
    }

    function removeToast(id: string) {
        setToasts(prev => {
            const li = [...prev];
            const map = li.map(v => v.id);
            
            if (!map.includes(id)) return prev;

            li.splice(map.indexOf(id), 1);

            return li;
        });
    }

    return (
        <ToasterContext.Provider value={{
            makeToast,
            notifications,
            removeNotification,
            clearNotifications
        }}>
            {children}

            <div className="Toaster">
                {toasts.map(v => (
                    <Toast key={`toast-${v.id}`} toast={v} onClose={removeToast} />
                ))}
            </div>
        </ToasterContext.Provider>
    );
}